import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Socket } from 'socket.io-client';
import { socketService } from 'services/socketService';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RootState } from 'store';
import { addPendingWithdrawal, removePendingWithdrawal } from 'store/transactionsSlice';
import notificationSound from '../../assets/sounds/notification.mp3';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (!isAuthenticated) {
      socketService.disconnect();
      setSocket(null);
      setIsConnected(false);
      return;
    }

    const initSocket = async () => {
      const s = await socketService.connect();
      setSocket(s);

      if (s.connected) {
        setIsConnected(true);
      }

      s.on('connect', () => {
        setIsConnected(true);
      });

      s.on('disconnect', () => {
        setIsConnected(false);
      });

      s.on('withdraw.approval', (data) => {
        const transaction = data.transaction || data;
        if (transaction && transaction.id) {
          if (transaction.status === 'cancelled') {
            dispatch(removePendingWithdrawal(transaction.id));
          } else {
            const audio = new Audio(notificationSound);
            audio.play().catch((err) => console.error('SocketProvider: Error playing sound:', err));

            dispatch(addPendingWithdrawal(transaction));
          }
        }
      });
    };

    initSocket();
  }, [isAuthenticated, dispatch]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>{children}</SocketContext.Provider>
  );
};
